import { usePlan } from '../../contexts/PlanContext';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  FolderOpen,
  FileText,
  Settings,
  LogOut,
  ChevronDown,
  Wallet,
  Users,
  CreditCard,
  BarChart2,
  Menu,
  Sun,
  Moon,
  Bell,
  BriefcaseIcon,
  CheckCheck,
  Briefcase,
  X
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../../contexts/ThemeContext';

const API_URL_NOTIF = process.env.REACT_APP_BACKEND_URL;

// Relative time formatter
function timeAgo(isoStr) {
  if (!isoStr) return '';
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
}

const NOTIF_ICON = {
  job_assigned:  { icon: Briefcase,  cls: 'text-blue-500'   },
  job_completed: { icon: CheckCheck, cls: 'text-green-500'  },
  job_declined:  { icon: X,          cls: 'text-red-500'    },
  review_ready:  { icon: FileText,   cls: 'text-orange-500' },
};

function NotificationBell({ isDarkMode }) {
  const [open,         setOpen]         = useState(false);
  const [notifs,       setNotifs]       = useState([]);
  const [unreadCount,  setUnreadCount]  = useState(0);
  const [loading,      setLoading]      = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const fetchCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL_NOTIF}/api/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { const d = await res.json(); setUnreadCount(d.unread_count || 0); }
    } catch {}
  }, [token]);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL_NOTIF}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const d = await res.json();
        setNotifs(d.notifications || []);
        setUnreadCount(d.unread_count || 0);
      }
    } catch {}
    setLoading(false);
  }, [token]);

  // Poll count every 60s
  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => clearInterval(id);
  }, [fetchCount]);

  // Fetch full list when panel opens
  useEffect(() => { if (open) fetchAll(); }, [open, fetchAll]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (notif) => {
    if (!notif.read) {
      try {
        await fetch(`${API_URL_NOTIF}/api/notifications/mark-read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ notif_ids: [notif.notif_id] })
        });
        setNotifs(prev => prev.map(n => n.notif_id === notif.notif_id ? {...n, read: true} : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch {}
    }
    if (notif.link) { navigate(notif.link); setOpen(false); }
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API_URL_NOTIF}/api/notifications/mark-all-read`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      setNotifs(prev => prev.map(n => ({...n, read: true})));
      setUnreadCount(0);
    } catch {}
  };

  const clearRead = async () => {
    try {
      await fetch(`${API_URL_NOTIF}/api/notifications/clear`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      setNotifs(prev => prev.filter(n => !n.read));
    } catch {}
  };

  return (
    <div ref={panelRef} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className={`relative p-2 rounded-xl transition-colors ${
          isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
        } ${open ? (isDarkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}`}
        title="Notifications"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] flex items-center justify-center rounded-full bg-blue-600 text-white text-[10px] font-bold px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className={`absolute right-0 top-full mt-2 w-80 rounded-2xl shadow-2xl border z-50 flex flex-col max-h-[480px] ${
          isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {/* Header */}
          <div className={`flex items-center justify-between px-4 py-3 border-b flex-shrink-0 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              Notifications {unreadCount > 0 && <span className="ml-1 text-xs font-bold text-blue-600">{unreadCount} new</span>}
            </span>
            <div className="flex gap-1">
              {unreadCount > 0 && (
                <button onClick={markAllRead}
                  className={`text-xs px-2 py-1 rounded-lg transition-colors ${isDarkMode ? 'text-blue-400 hover:bg-gray-800' : 'text-blue-600 hover:bg-blue-50'}`}>
                  Mark all read
                </button>
              )}
              {notifs.some(n => n.read) && (
                <button onClick={clearRead}
                  className={`text-xs px-2 py-1 rounded-lg transition-colors ${isDarkMode ? 'text-gray-500 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}>
                  Clear read
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className={`flex items-center justify-center py-10 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Loading...</div>
            ) : notifs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <Bell size={28} className={isDarkMode ? 'text-gray-600' : 'text-gray-300'} />
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No notifications</p>
              </div>
            ) : (
              notifs.map(n => {
                const cfg = NOTIF_ICON[n.type] || NOTIF_ICON.job_assigned;
                const Icon = cfg.icon;
                return (
                  <button
                    key={n.notif_id}
                    onClick={() => markRead(n)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors border-b last:border-b-0 ${
                      isDarkMode ? 'border-gray-800 hover:bg-gray-800' : 'border-gray-100 hover:bg-gray-50'
                    } ${!n.read ? (isDarkMode ? 'bg-blue-900/10' : 'bg-blue-50/60') : ''}`}
                  >
                    <div className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center mt-0.5 ${
                      isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
                    }`}>
                      <Icon size={14} className={cfg.cls} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium leading-tight truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{n.title}</p>
                      <p className={`text-xs mt-0.5 truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{n.message}</p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NavbarApple = ({ user, planInfo: planInfoProp, onLogout, onToggleSidebar, isDarkMode: isDarkModeProp }) => {
  const { planInfo: planInfoCtx } = usePlan();
  const planInfo = planInfoCtx || planInfoProp;
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [avatar, setAvatar] = useState(() => localStorage.getItem('user_avatar'));
  const [showAvatarPreview, setShowAvatarPreview] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const themeContext = useTheme();
  const isDarkMode = isDarkModeProp !== undefined ? isDarkModeProp : themeContext.isDarkMode;

  React.useEffect(() => {
    const handler = (e) => setAvatar(e.detail);
    window.addEventListener('avatar-updated', handler);
    return () => window.removeEventListener('avatar-updated', handler);
  }, []);

  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/api/user/avatar`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        if (data.avatar) {
          setAvatar(data.avatar);
          localStorage.setItem('user_avatar', data.avatar);
        } else {
          setAvatar(null);
          localStorage.removeItem('user_avatar');
        }
      })
      .catch(() => {});
  }, []);

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const getInitials = (user) => {
    const emailParts = (user?.email || '').split('@')[0].split(/[._-]/);
    if (user?.name) {
      const nameParts = user.name.trim().split(/\s+/);
      if (nameParts.length >= 2) return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
      // Single name: use email second segment for second initial
      if (emailParts.length >= 2 && emailParts[1]) return (nameParts[0][0] + emailParts[1][0]).toUpperCase();
      return nameParts[0].slice(0, 2).toUpperCase();
    }
    if (emailParts.length >= 2 && emailParts[1]) return (emailParts[0][0] + emailParts[1][0]).toUpperCase();
    if (user?.email) return user.email.slice(0, 2).toUpperCase();
    return 'U';
  };

  const getPlanBadge = () => {
    if (!planInfo) return null;
    const plan = planInfo.plan || 'free';
    const planClasses = {
      free: 'free',
      starter: 'starter',
      professional: 'professional',
      business: 'business',
      payg: 'payg'
    };
    return (
      <span className={`apple-plan-badge ${planClasses[plan] || 'free'}`}>
        {plan.toUpperCase()}
      </span>
    );
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="apple-navbar" data-testid="apple-navbar">
      {/* Mobile Menu Toggle - FIX 2: Hide on desktop (≥1024px) */}
      <button 
        className="apple-btn apple-btn-ghost apple-btn-icon mobile-menu-btn"
        onClick={onToggleSidebar}
        data-testid="mobile-menu-toggle"
        style={{
          display: 'flex',  // Default: visible on mobile
        }}
      >
        <Menu size={20} />
      </button>

      {/* Inline style to hide on desktop - more specific than Tailwind */}
      <style>{`
        @media (min-width: 1024px) {
          [data-testid="mobile-menu-toggle"] {
            display: none !important;
          }
        }
      `}</style>

      {/* Brand */}
      <div className="apple-navbar-brand">
        <Link to="/" className="flex items-center gap-3" data-testid="navbar-brand-link">
          <div style={{
            width: '44px',
            height: '44px',
            background: '#000',
            borderRadius: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: isDarkMode
              ? '0 0 0 2px #ffffff'
              : '0 0 0 1px rgba(0,86,255,.30), 0 4px 20px rgba(0,86,255,.22)'
          }}>
            <img
              src="/logo-marketing.png"
              alt="t09n.com"
              style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: 0, display: 'block' }}
            />
          </div>
          <div className="hidden sm:flex flex-col" style={{ gap: 0 }}>
            <span style={{
              fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: 700,
              fontStyle: 'normal',
              fontSize: '18px',
              color: isDarkMode ? '#ffffff' : '#000000',
              letterSpacing: '-0.3px',
              lineHeight: 1.1
            }}>
              t09n<span style={{ fontWeight: 700 }}>.com</span>
            </span>
            <span style={{
              fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif',
              marginTop: '3px',
              fontSize: '11px',
              color: isDarkMode ? '#ffffff' : '#000000',
              fontWeight: 700,
              fontStyle: 'oblique',
              lineHeight: 1.2,
              letterSpacing: '0.1px'
            }}>
              The Operating System For Translators
            </span>
          </div>
        </Link>
      </div>

      {/* Center Nav - Hidden on mobile */}
      <div className="apple-navbar-nav hidden md:flex">
        <Link 
          to="/" 
          className={`apple-navbar-link ${isActive('/') ? 'active' : ''}`}
          data-testid="navbar-dashboard-link"
        >
          <Home size={18} />
          <span>Dashboard</span>
        </Link>
        <Link 
          to="/projects" 
          className={`apple-navbar-link ${isActive('/projects') ? 'active' : ''}`}
          data-testid="navbar-projects-link"
        >
          <FolderOpen size={18} />
          <span>Projects</span>
        </Link>
        <Link 
          to="/cat" 
          className={`apple-navbar-link ${isActive('/cat') ? 'active' : ''}`}
          data-testid="navbar-cat-link"
        >
          <FileText size={18} />
          <span>CAT Tool</span>
        </Link>
      </div>

      {/* Actions */}
      <div className="apple-navbar-actions">
        {/* Theme Toggle */}
        <ThemeToggle />

        {/* Notification Bell */}
        <NotificationBell isDarkMode={isDarkMode} />

        {/* Plan Badge */}
        {getPlanBadge()}
        
        {/* Balance / Usage - Hidden on small screens */}
        {planInfo && (
          <div
            className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full text-sm"
            style={{ background: isDarkMode ? '#1e293b' : '#f1f5f9', color: isDarkMode ? '#94a3b8' : '#475569' }}
            data-testid="navbar-balance-display"
          >
            <Wallet size={14} />
            {planInfo.plan === 'payg' ? (
              /* PAYG — show words remaining only */
              <span style={{ fontWeight: 700, fontSize: 13, color: isDarkMode ? '#f1f5f9' : '#111827' }}>
                {(planInfo.words_remaining || planInfo.wallet_words || 0).toLocaleString()} words left
              </span>
            ) : (
              /* Subscription — show used/total + progress bar */
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 140 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                  <span style={{ fontWeight: 600, color: isDarkMode ? '#f1f5f9' : '#111827' }}>
                    {(planInfo.monthly_usage || 0).toLocaleString()} / {(planInfo.monthly_quota || 0).toLocaleString()}
                  </span>
                  <span style={{ color: (planInfo.usage_percent || 0) >= 90 ? '#ef4444' : (planInfo.usage_percent || 0) >= 70 ? '#f59e0b' : '#6b7280', marginLeft: 6, fontWeight: 600 }}>
                    {planInfo.usage_percent || 0}%
                  </span>
                </div>
                <div style={{ width: '100%', height: 4, borderRadius: 2, background: isDarkMode ? '#334155' : '#e2e8f0', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 2,
                    width: `${Math.min(planInfo.usage_percent || 0, 100)}%`,
                    background: (planInfo.usage_percent || 0) >= 90 ? '#ef4444' : (planInfo.usage_percent || 0) >= 70 ? '#f59e0b' : '#2563eb',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* User Menu */}
        <div className="relative">
          <button
            className="apple-navbar-user"
            onClick={() => { setShowUserMenu(!showUserMenu); setShowAvatarPreview(false); }}
            onMouseEnter={() => { if (avatar && !showUserMenu) setShowAvatarPreview(true); }}
            onMouseLeave={() => setShowAvatarPreview(false)}
            data-testid="navbar-user-menu-trigger"
          >
            <div
              className="apple-navbar-avatar"
              style={{ overflow: 'hidden', padding: 0, flexShrink: 0 }}
            >
              {avatar
                ? <img
                    src={avatar}
                    alt="avatar"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      borderRadius: '50%',
                      display: 'block',
                      imageRendering: 'auto',
                    }}
                  />
                : getInitials(user)
              }
            </div>
            <ChevronDown 
              size={16} 
              className={`text-gray-400 transition-transform hidden sm:block ${showUserMenu ? 'rotate-180' : ''}`} 
            />
          </button>

          {/* Avatar hover preview — only when photo uploaded, dropdown closed */}
          {showAvatarPreview && !showUserMenu && avatar && (
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: 'calc(100% + 10px)',
                zIndex: 45,
                pointerEvents: 'none',
                animation: 'appleFadeIn 0.12s ease',
              }}
            >
              <div style={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                overflow: 'hidden',
                boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
                border: `3px solid ${isDarkMode ? '#374151' : '#fff'}`,
              }}>
                <img
                  src={avatar}
                  alt="avatar preview"
                  style={{
                    display: 'block',
                    width: '100px',
                    height: '100px',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    imageRendering: 'auto',
                  }}
                />
              </div>
            </div>
          )}

          {showUserMenu && (
            <div 
              className="apple-dropdown-menu absolute right-0 top-full mt-2 w-56 rounded-xl shadow-lg py-2 z-50"
              data-testid="navbar-user-dropdown"
              style={{ 
                animation: 'appleFadeIn 0.15s ease',
                background: 'var(--apple-bg-elevated)',
                border: '1px solid var(--apple-border-primary)'
              }}
            >
              <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--apple-separator)' }}>
                <p className="text-sm font-medium truncate" style={{ color: 'var(--apple-text-primary)' }}>{user?.email}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--apple-text-tertiary)' }}>Logged in</p>
              </div>
              
              <div className="py-1">
                <Link 
                  to="/settings" 
                  className="apple-dropdown-item flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
                  onClick={() => setShowUserMenu(false)}
                  data-testid="user-menu-settings"
                  style={{ color: 'var(--apple-text-secondary)' }}
                >
                  <Settings size={16} />
                  Settings
                </Link>
                <Link 
                  to="/pricing" 
                  className="apple-dropdown-item flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
                  onClick={() => setShowUserMenu(false)}
                  data-testid="user-menu-pricing"
                  style={{ color: 'var(--apple-text-secondary)' }}
                >
                  <CreditCard size={16} />
                  Pricing & Plans
                </Link>
                <Link
                  to="/billing"
                  className="apple-dropdown-item flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
                  onClick={() => setShowUserMenu(false)}
                  data-testid="user-menu-billing"
                  style={{ color: 'var(--apple-text-secondary)' }}
                >
                  <CreditCard size={16} />
                  Billing
                </Link>
                <Link
                  to="/usage"
                  className="apple-dropdown-item flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
                  onClick={() => setShowUserMenu(false)}
                  data-testid="user-menu-usage"
                  style={{ color: 'var(--apple-text-secondary)' }}
                >
                  <BarChart2 size={16} />
                  Usage
                </Link>
                {planInfo?.plan === 'business' && (
                  <Link 
                    to="/team" 
                    className="apple-dropdown-item flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
                    onClick={() => setShowUserMenu(false)}
                    data-testid="user-menu-team"
                    style={{ color: 'var(--apple-text-secondary)' }}
                  >
                    <Users size={16} />
                    Team Management
                  </Link>
                )}
              </div>
              
              <div style={{ borderTop: '1px solid var(--apple-separator)', paddingTop: '4px' }}>
                <button 
                  className="apple-dropdown-item flex items-center gap-3 px-4 py-2.5 text-sm w-full transition-colors"
                  onClick={handleLogout}
                  data-testid="user-menu-logout"
                  style={{ color: 'var(--apple-red)' }}
                >
                  <LogOut size={16} />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close */}
      {showUserMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onMouseDown={() => setShowUserMenu(false)}
        />
      )}
    </nav>
  );
};

export default NavbarApple;
